import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Link, useNavigate } from 'react-router-dom';
import { RiMapPinLine, RiCloseLine, RiExternalLinkLine, RiArrowDownSLine } from 'react-icons/ri';
import './Checkout.css';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { usePublicSettings } from '../../context/PublicSettingsContext';
import { useStorefront } from '../../context/StoreContext.js';
import AuthContext from '../../context/AuthContext';
import LanguageContext from '../../context/LanguageContext.js';
import { authAPI } from '../../api/authApi';
import { createFoodOrder, validateCouponCode } from '../../api/orderApi';
import { fetchMenuItemById } from '../../api/menuApi';
import { getVariantSizeDisplay } from '../../utils/variantDisplay';
import {
    computeDiscountAmountPreview,
    computeOrderBreakdown,
    formatTaxRateForLabel
} from '../../utils/orderPricing';
import { getExpectedStoreType } from '../../lib/storefront/storeBootstrap.js';
import { useStorePath } from '../../lib/storefront/storePath.js';
import { rememberPendingOnlinePayment } from '../../utils/pendingOnlinePayment';

const CHECKOUT_BILLING_STORAGE_KEY = 'quazbitCheckoutBilling';

/** Payment options, each shown only while the merchant has its setting turned on. */
const PAYMENT_OPTIONS = [
    { value: 'cash', setting: 'cashOnDelivery', labelKey: 'checkout.cashOnDelivery' },
    { value: 'online', setting: 'online', labelKey: 'checkout.onlinePayment' }
];

// What the storefront offers for stores saved before they had payment settings.
const DEFAULT_PAYMENT_METHODS = { cashOnDelivery: true, online: false };

const ORDER_TYPE_OPTIONS = [
    { value: 'delivery', titleKey: 'checkout.delivery', descriptionKey: 'checkout.deliveryDescription' },
    { value: 'pickup', titleKey: 'checkout.pickup', descriptionKey: 'checkout.pickupDescription' },
    { value: 'dine-in', titleKey: 'checkout.dineIn', descriptionKey: 'checkout.dineInDescription' }
];

/**
 * Fulfilment methods this store actually offers.
 * Delivery and pickup follow the merchant's settings toggles; dine-in is a
 * restaurant-only concept, so it is gated on the store type.
 */
function getAvailableOrderTypes(publicSettings, storeType) {
    const types = [];
    // Undefined means the store predates the toggles — treat those as enabled.
    if (publicSettings?.deliveryEnabled !== false) types.push('delivery');
    if (publicSettings?.pickupEnabled !== false) types.push('pickup');
    // Dine-in is restaurant-only, and the merchant can still switch it off.
    if (storeType === 'restaurant' && publicSettings?.dineInEnabled !== false) types.push('dine-in');
    return types;
}

const BILLING_SNAPSHOT_KEYS = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'street',
    'apartment',
    'city',
    'postalCode',
    'country',
    'orderType',
    'tableNumber',
    'paymentMethod'
];

function readStoredCheckoutBilling() {
    try {
        const raw = localStorage.getItem(CHECKOUT_BILLING_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
}

const VALID_ORDER_TYPES = new Set(['delivery', 'pickup', 'dine-in']);
const VALID_PAYMENT_METHODS = new Set(['cash', 'online']);

function mergeSavedBilling(saved, defaults) {
    if (!saved) return defaults;
    const next = { ...defaults };
    for (const key of BILLING_SNAPSHOT_KEYS) {
        const v = saved[key];
        if (v == null || String(v).trim() === '') continue;
        if (key === 'orderType' && !VALID_ORDER_TYPES.has(v)) continue;
        if (key === 'paymentMethod' && !VALID_PAYMENT_METHODS.has(v)) continue;
        next[key] = v;
    }
    return next;
}

function splitFullName(name) {
    const trimmed = String(name || '').trim();
    if (!trimmed) return { firstName: '', lastName: '' };
    const space = trimmed.indexOf(' ');
    if (space === -1) return { firstName: trimmed, lastName: '' };
    return {
        firstName: trimmed.slice(0, space),
        lastName: trimmed.slice(space + 1).trim()
    };
}

/** Map a single profile address string into checkout fields (comma-separated lines when possible). */
function parseProfileAddress(address) {
    const raw = String(address || '').trim();
    if (!raw) return {};
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 3) {
        return {
            street: parts[0],
            city: parts[1],
            country: parts.slice(2).join(', ')
        };
    }
    if (parts.length === 2) {
        return { street: parts[0], city: parts[1] };
    }
    return { street: raw };
}

function buildBillingSnapshot(form) {
    const snap = {};
    for (const key of BILLING_SNAPSHOT_KEYS) {
        snap[key] = form[key];
    }
    return snap;
}

const Checkout = () => {
    const { dir } = useContext(LanguageContext);
    const { t } = useTranslation();
    const currentLanguage = i18n.language;
    const { cartItems, getCartTotal, clearCart, removeFromCart, cartRemainingTime, formatTime } = useCart();
    const { formatPrice, publicSettings, currencyCode } = usePublicSettings();
    const { store } = useStorefront();
    const { addToast } = useToast();
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const storePath = useStorePath();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [couponFeedback, setCouponFeedback] = useState({ type: '', message: '' });
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
    const [deliveryCoords, setDeliveryCoords] = useState(null);
    const [isLocating, setIsLocating] = useState(false);

    const [formData, setFormData] = useState(() => {
        const defaults = {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            street: '',
            apartment: '',
            city: '',
            postalCode: '',
            // Filled with the store's own country once settings load (see below).
            country: '',
            orderType: 'delivery',
            tableNumber: '',
            paymentMethod: 'cash',
            customerNotes: ''
        };
        return mergeSavedBilling(readStoredCheckoutBilling(), defaults);
    });

    // The store may still be resolving; fall back to the type this build targets.
    const storeType = store?.type || getExpectedStoreType();

    const availableOrderTypes = useMemo(
        () => getAvailableOrderTypes(publicSettings, storeType),
        [publicSettings, storeType]
    );

    // Delivery is local: default the country to the store's, unless the customer already has one.
    const storeCountry = publicSettings?.address?.country;
    useEffect(() => {
        if (!storeCountry) return;
        setFormData((prev) => (prev.country ? prev : { ...prev, country: storeCountry }));
    }, [storeCountry]);

    const orderTypeAvailable = availableOrderTypes.includes(formData.orderType);
    const noOrderTypesAvailable = availableOrderTypes.length === 0;

    // A saved or default selection can point at a method the merchant has since
    // turned off — move it to the first one that is actually offered.
    useEffect(() => {
        if (noOrderTypesAvailable || orderTypeAvailable) return;
        setFormData((prev) => ({ ...prev, orderType: availableOrderTypes[0] }));
    }, [noOrderTypesAvailable, orderTypeAvailable, availableOrderTypes]);

    const enabledPaymentMethods = publicSettings?.paymentMethods || DEFAULT_PAYMENT_METHODS;
    const paymentOptions = PAYMENT_OPTIONS.filter((option) => enabledPaymentMethods[option.setting]);
    const paymentMethodAvailable = paymentOptions.some((option) => option.value === formData.paymentMethod);
    const firstPaymentMethod = paymentOptions[0]?.value;

    // Same for payment: the saved choice may have been turned off since.
    useEffect(() => {
        if (paymentMethodAvailable || !firstPaymentMethod) return;
        setFormData((prev) => ({ ...prev, paymentMethod: firstPaymentMethod }));
    }, [paymentMethodAvailable, firstPaymentMethod]);

    const subtotal = getCartTotal();
    const defaultDeliveryFee = publicSettings?.defaultDeliveryFee ?? 0;
    const taxRatePercent = publicSettings?.taxRatePercent ?? 0;
    const pricesIncludeTax = Boolean(publicSettings?.pricesIncludeTax);

    const discountForPricing = appliedCoupon
        ? {
            code: appliedCoupon.code,
            discountAmount: appliedCoupon.discountAmount,
            discountPercentage: appliedCoupon.discountPercentage
        }
        : { code: 'None', discountAmount: 0, discountPercentage: 0 };

    const {
        deliveryFee,
        tax,
        totalAmount,
        discountAmount: pricingDiscountAmount
    } = computeOrderBreakdown({
        subtotal,
        orderType: formData.orderType,
        discount: discountForPricing,
        defaultDeliveryFee,
        taxRatePercent,
        pricesIncludeTax,
        serviceFee: 0,
        tip: 0,
        currency: currencyCode
    });

    const taxLabelRate = formatTaxRateForLabel(taxRatePercent);
    const taxRowLabel =
        Number(taxRatePercent) > 0
            ? t(pricesIncludeTax ? 'checkout.taxIncludedRate' : 'checkout.taxRate', { rate: taxLabelRate })
            : t('checkout.tax');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (!user?._id) return;
        let cancelled = false;
        (async () => {
            try {
                const response = await authAPI.getUserData();
                const userData = response?.data?.data || response?.data;
                if (cancelled || !userData) return;
                const { firstName, lastName } = splitFullName(userData.name);
                const fromAddr = parseProfileAddress(userData.profile?.address);
                setFormData((prev) => ({
                    ...prev,
                    firstName: firstName || prev.firstName,
                    lastName: lastName || prev.lastName,
                    email: userData.email || prev.email,
                    phone: userData.profile?.phone || prev.phone,
                    street: fromAddr.street || prev.street,
                    apartment: fromAddr.apartment || prev.apartment,
                    city: fromAddr.city || prev.city,
                    country: fromAddr.country || prev.country
                }));
            } catch {
                /* profile autofill is best-effort */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user?._id]);

    useEffect(() => {
        if (formData.orderType !== 'delivery') {
            setDeliveryCoords(null);
            setIsLocating(false);
        }
    }, [formData.orderType]);

    const handleShareLocation = () => {
        if (!navigator.geolocation) {
            addToast(t('checkout.locationNotSupported'), 'error');
            return;
        }
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setIsLocating(false);
                setDeliveryCoords({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude
                });
                addToast(t('checkout.locationCaptured'), 'success');
            },
            (err) => {
                setIsLocating(false);
                if (err && err.code === 1) {
                    addToast(t('checkout.locationDenied'), 'error');
                } else {
                    addToast(t('checkout.locationUnavailable'), 'error');
                }
            },
            { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 }
        );
    };

    const handleClearDeliveryLocation = () => {
        setDeliveryCoords(null);
    };

    useEffect(() => {
        if (!appliedCoupon) return;
        const hasOrderTypeChanged = appliedCoupon.appliedForOrderType !== formData.orderType;
        const hasSubtotalChanged = Number(appliedCoupon.appliedForSubtotal) !== Number(subtotal);

        if (!hasOrderTypeChanged && !hasSubtotalChanged) return;

        setAppliedCoupon(null);
        setCouponFeedback({
            type: 'info',
            message: t('checkout.couponRemovedDueToChanges')
        });
    }, [appliedCoupon, formData.orderType, subtotal, t]);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) {
            setCouponFeedback({ type: 'error', message: t('checkout.enterCouponCode') });
            return;
        }

        setIsApplyingCoupon(true);
        setCouponFeedback({ type: '', message: '' });

        try {
            const response = await validateCouponCode({
                code: couponCode.trim().toUpperCase(),
                subtotal,
                orderType: formData.orderType
            });

            const discount = response?.data?.appliedDiscount;
            const nextCoupon = {
                code: discount?.code || couponCode.trim().toUpperCase(),
                discountAmount: Number(discount?.discountAmount || 0),
                discountPercentage: Number(discount?.discountPercentage || 0),
                appliedForOrderType: formData.orderType,
                appliedForSubtotal: subtotal
            };
            setAppliedCoupon(nextCoupon);
            const savedAmount = computeDiscountAmountPreview(subtotal, {
                code: nextCoupon.code,
                discountAmount: nextCoupon.discountAmount,
                discountPercentage: nextCoupon.discountPercentage
            });
            setCouponFeedback({
                type: 'success',
                message: t('checkout.couponAppliedSuccess', { amount: formatPrice(savedAmount) })
            });
        } catch (error) {
            setAppliedCoupon(null);
            setCouponFeedback({
                type: 'error',
                message: error?.message || t('checkout.invalidCoupon')
            });
        } finally {
            setIsApplyingCoupon(false);
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponFeedback({ type: 'info', message: t('checkout.couponRemoved') });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!user || !user._id) {
            addToast(t('checkout.loginRequired'), 'error');
            return;
        }

        if (cartItems.length === 0) {
            addToast(t('checkout.emptyCart'), 'error');
            return;
        }

        // Validate cart items exist and are available
        const invalidItems = [];
        for (const item of cartItems) {
            try {
                await fetchMenuItemById(item._id || item.id);
            } catch (error) {
                invalidItems.push(item);
            }
        }

        if (invalidItems.length > 0) {
            invalidItems.forEach(item => removeFromCart(item._id || item.id));
            addToast(t('checkout.invalidItemsRemoved'), 'warning');
            setIsSubmitting(false);
            return;
        }

        if (!availableOrderTypes.includes(formData.orderType)) {
            addToast(t('checkout.orderTypeUnavailable'), 'error');
            return;
        }

        if (!paymentMethodAvailable) {
            addToast(t('checkout.noPaymentMethods'), 'error');
            return;
        }

        setIsSubmitting(true);
        let leavingForPayment = false;

        try {
            // Map cart items to food order items format
            const orderItems = cartItems.map(item => ({
                menuItemId: item._id || item.id,
                name: item.name,
                description: item.description,
                quantity: item.quantity,
                price: item.price,
                category: item.category,
                subCategory: item.subCategory,
                primaryImage: (Array.isArray(item.imageUrl) ? item.imageUrl[0] : item.imageUrl) || (Array.isArray(item.image) ? item.image[0] : item.image) || '',
                variant: item.selectedVariant || {},
                specialInstructions: item.specialInstructions || '',
                isVegetarian: item.isVegetarian || false,
                isVegan: item.isVegan || false,
                isGlutenFree: item.isGlutenFree || false,
                spiceLevel: item.spiceLevel || 'none'
            }));

            const orderData = {
                customerInfo: {
                    fullName: `${formData.firstName} ${formData.lastName}`,
                    email: formData.email,
                    phone: formData.phone,
                    orderType: formData.orderType,
                    deliveryAddress: formData.orderType === 'delivery' ? {
                        street: formData.street,
                        apartment: formData.apartment,
                        city: formData.city,
                        postalCode: formData.postalCode,
                        country: formData.country,
                        ...(deliveryCoords
                            ? { coordinates: { lat: deliveryCoords.lat, lng: deliveryCoords.lng } }
                            : {})
                    } : undefined,
                    tableNumber: formData.orderType === 'dine-in' ? formData.tableNumber : undefined
                },
                userId: user?._id || null, // Associate order with logged-in user if available
                items: orderItems,
                couponCode: appliedCoupon?.code || undefined,
                discount: appliedCoupon
                    ? {
                        code: appliedCoupon.code,
                        discountAmount: appliedCoupon.discountAmount,
                        discountPercentage: appliedCoupon.discountPercentage,
                        appliedBy: 'customer'
                    }
                    : {
                        code: 'None',
                        discountAmount: 0,
                        discountPercentage: 0
                    },
                serviceFee: 0,
                tip: 0,
                payment: {
                    paymentMethod: formData.paymentMethod
                },
                // Where the payment provider sends the customer back after paying online
                returnPath: storePath(`/${currentLanguage}/order-success`),
                delivery:
                    formData.orderType === 'delivery'
                        ? {
                            deliveryStatus: 'pending'
                        }
                        : undefined,
                customerNotes: formData.customerNotes,
                priority: 'normal'
            };

            const response = await createFoodOrder(orderData);

            if (response.status === 'success') {
                try {
                    localStorage.setItem(
                        CHECKOUT_BILLING_STORAGE_KEY,
                        JSON.stringify(buildBillingSnapshot(formData))
                    );
                } catch {
                    /* ignore quota / private mode */
                }

                const orderNumber = response.data.orderNumber;

                if (formData.paymentMethod === 'online') {
                    const redirectUrl = response.checkout?.redirectUrl;
                    if (!redirectUrl) {
                        addToast(t('checkout.onlinePaymentUnavailable'), 'error');
                        return;
                    }
                    // The cart stays until the payment is confirmed, so a failed payment can be retried.
                    rememberPendingOnlinePayment(orderNumber);
                    leavingForPayment = true;
                    window.location.assign(redirectUrl);
                    return;
                }

                addToast(t('checkout.orderSuccess'), 'success');
                clearCart();

                // Redirect to order tracking or success page
                navigate(`/${currentLanguage}/order-success?orderNumber=${orderNumber}`);
            }
        } catch (error) {
            console.error('Error placing order:', error);
            addToast(
                error?.message || error?.response?.data?.message || t('checkout.orderError'),
                'error'
            );
        } finally {
            // Keep the button disabled while the browser leaves for the payment page.
            if (!leavingForPayment) setIsSubmitting(false);
        }
    };

    const layoutDir = dir();

    return (
        <main className="main">
            <Breadcrumb title={t('checkout.title')} />

            <section className="checkout-page">
                <div className="container checkout-layout" dir={layoutDir}>
                    <form className="checkout-form" onSubmit={handleSubmit}>
                        <div className='checkout-sections'>
                            <div className='checkout-section'>
                                <h3 className='checkout-section-title'>{t('checkout.orderType')}</h3>

                                {noOrderTypesAvailable ? (
                                    <p className='order-type-empty'>{t('checkout.noOrderTypes')}</p>
                                ) : (
                                    <div className='order-type-options'>
                                        {ORDER_TYPE_OPTIONS.filter((option) =>
                                            availableOrderTypes.includes(option.value)
                                        ).map((option) => (
                                            <label
                                                key={option.value}
                                                className={`order-type-option${formData.orderType === option.value ? ' order-type-option--active' : ''}`}
                                            >
                                                <input
                                                    type='radio'
                                                    name='orderType'
                                                    value={option.value}
                                                    checked={formData.orderType === option.value}
                                                    onChange={handleInputChange}
                                                />
                                                <div className='order-type-content'>
                                                    <span className='order-type-title'>{t(option.titleKey)}</span>
                                                    <span className='order-type-description'>{t(option.descriptionKey)}</span>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className='checkout-section'>
                                <h3 className='checkout-section-title'>{t('checkout.billingDetails')}</h3>

                                <div className='form-grid'>
                                    <div className='form-group'>
                                        <label htmlFor='firstName'>{t('checkout.firstName')}</label>
                                        <input
                                            type='text'
                                            id='firstName'
                                            name='firstName'
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            autoComplete='given-name'
                                            required
                                        />
                                    </div>

                                    <div className='form-group'>
                                        <label htmlFor='lastName'>{t('checkout.lastName')}</label>
                                        <input
                                            type='text'
                                            id='lastName'
                                            name='lastName'
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            autoComplete='family-name'
                                            required
                                        />
                                    </div>
                                </div>

                                <div className='form-group'>
                                    <label htmlFor='email'>{t('checkout.email')}</label>
                                    <input
                                        type='email'
                                        id='email'
                                        name='email'
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        autoComplete='email'
                                        required
                                    />
                                </div>

                                <div className='form-group'>
                                    <label htmlFor='phone'>{t('checkout.phone')}</label>
                                    <input
                                        type='tel'
                                        id='phone'
                                        name='phone'
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        autoComplete='tel'
                                        required
                                    />
                                </div>

                                {formData.orderType === 'delivery' && (
                                    <>
                                        <div className='form-group'>
                                            <label htmlFor='street'>{t('checkout.streetAddress')}</label>
                                            <input
                                                type='text'
                                                id='street'
                                                name='street'
                                                value={formData.street}
                                                onChange={handleInputChange}
                                                autoComplete='street-address'
                                                required
                                            />
                                        </div>

                                        <div className='form-group'>
                                            <label htmlFor='apartment'>{t('checkout.apartment')}</label>
                                            <input
                                                type='text'
                                                id='apartment'
                                                name='apartment'
                                                value={formData.apartment}
                                                onChange={handleInputChange}
                                                autoComplete='address-line2'
                                            />
                                        </div>

                                        <div className='form-grid'>
                                            <div className='form-group'>
                                                <label htmlFor='city'>{t('checkout.city')}</label>
                                                <input
                                                    type='text'
                                                    id='city'
                                                    name='city'
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    autoComplete='address-level2'
                                                    required
                                                />
                                            </div>

                                            {/*<div className='form-group'>*/}
                                            {/*    <label htmlFor='postalCode'>{t('checkout.postalCode')}</label>*/}
                                            {/*    <input*/}
                                            {/*        type='text'*/}
                                            {/*        id='postalCode'*/}
                                            {/*        name='postalCode'*/}
                                            {/*        value={formData.postalCode}*/}
                                            {/*        onChange={handleInputChange}*/}
                                            {/*        required*/}
                                            {/*    />*/}
                                            {/*</div>*/}

                                            <div className='form-group'>
                                                <label htmlFor='country'>{t('checkout.country')}</label>
                                                <input
                                                    type='text'
                                                    id='country'
                                                    name='country'
                                                    value={formData.country}
                                                    onChange={handleInputChange}
                                                    autoComplete='country-name'
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className='checkout-location-panel'>
                                            <div className='checkout-location-header'>
                                                <p className='checkout-location-title'>{t('checkout.deliveryLocationOption')}</p>
                                                {!deliveryCoords && (
                                                    <span className='checkout-location-badge'>{t('checkout.locationRecommended')}</span>
                                                )}
                                            </div>
                                            {!deliveryCoords ? (
                                                <>
                                                    <p className='checkout-location-cta'>
                                                        <RiArrowDownSLine className='checkout-location-cta-icon' aria-hidden />
                                                        {t('checkout.shareLocationCallToAction')}
                                                    </p>
                                                    <p className='checkout-location-hint'>{t('checkout.shareLocationHint')}</p>
                                                </>
                                            ) : (
                                                <p className='checkout-location-saved' role='status'>
                                                    {t('checkout.locationSavedHint')}
                                                </p>
                                            )}
                                            <div className='checkout-location-row'>
                                                <button
                                                    type='button'
                                                    className='checkout-location-btn checkout-location-btn--primary'
                                                    onClick={handleShareLocation}
                                                    disabled={isLocating || isSubmitting}
                                                >
                                                    <RiMapPinLine className='checkout-location-btn-icon' aria-hidden />
                                                    {isLocating
                                                        ? t('checkout.locationLoading')
                                                        : deliveryCoords
                                                            ? t('checkout.shareLocationUpdate')
                                                            : t('checkout.shareLocation')}
                                                </button>
                                                {deliveryCoords && (
                                                    <>
                                                    <span className='checkout-location-pill' title={t('checkout.locationCoords', { lat: deliveryCoords.lat.toFixed(5), lng: deliveryCoords.lng.toFixed(5) })}>
                                                        {t('checkout.locationCoords', { lat: deliveryCoords.lat.toFixed(5), lng: deliveryCoords.lng.toFixed(5) })}
                                                    </span>
                                                        <a
                                                            className='checkout-location-maps'
                                                            href={`https://www.google.com/maps?q=${deliveryCoords.lat},${deliveryCoords.lng}`}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                        >
                                                            <RiExternalLinkLine className='checkout-location-btn-icon' aria-hidden />
                                                            {t('checkout.locationOpenMaps')}
                                                        </a>
                                                        <button
                                                            type='button'
                                                            className='checkout-location-remove'
                                                            onClick={handleClearDeliveryLocation}
                                                            disabled={isSubmitting}
                                                            aria-label={t('checkout.removeLocation')}
                                                        >
                                                            <RiCloseLine aria-hidden />
                                                            <span>{t('checkout.removeLocation')}</span>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {formData.orderType === 'dine-in' && (
                                    <div className='form-group'>
                                        <label htmlFor='tableNumber'>{t('checkout.tableNumber')}</label>
                                        <input
                                            type='text'
                                            id='tableNumber'
                                            name='tableNumber'
                                            value={formData.tableNumber}
                                            onChange={handleInputChange}
                                            autoComplete='off'
                                            required
                                        />
                                    </div>
                                )}

                                <div className='form-group'>
                                    <label htmlFor='customerNotes'>{t('checkout.orderNotes')}</label>
                                    <textarea
                                        id='customerNotes'
                                        name='customerNotes'
                                        value={formData.customerNotes}
                                        onChange={handleInputChange}
                                        rows='4'
                                        autoComplete='off'
                                        placeholder={t('checkout.orderNotesPlaceholder')}
                                    />
                                </div>
                            </div>

                            <div className='checkout-section'>
                                <h3 className='checkout-section-title'>{t('checkout.paymentMethod')}</h3>

                                {paymentOptions.length === 0 ? (
                                    <p className='order-type-empty'>{t('checkout.noPaymentMethods')}</p>
                                ) : (
                                    <div className='payment-methods'>
                                        {paymentOptions.map((option) => (
                                            <label
                                                key={option.value}
                                                className={`payment-method${formData.paymentMethod === option.value ? ' payment-method--active' : ''}`}
                                            >
                                                <input
                                                    type='radio'
                                                    name='paymentMethod'
                                                    value={option.value}
                                                    checked={formData.paymentMethod === option.value}
                                                    onChange={handleInputChange}
                                                />
                                                <span>{t(option.labelKey)}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}

                                {formData.paymentMethod === 'online' && paymentMethodAvailable && (
                                    <p className='payment-method-hint'>{t('checkout.onlinePaymentHint')}</p>
                                )}
                            </div>
                        </div>

                        <div className="checkout-actions">
                            <Link to={`/${currentLanguage}/cart`} className="checkout-btn checkout-btn--outline">
                                {t('checkout.backToCart')}
                            </Link>
                            <button
                                type="submit"
                                className="checkout-btn checkout-btn--primary"
                                disabled={isSubmitting || cartItems.length === 0 || !paymentMethodAvailable}
                            >
                                <span>
                                    {isSubmitting
                                        ? t('checkout.processing')
                                        : formData.paymentMethod === 'online'
                                            ? t('checkout.continueToPayment')
                                            : t('checkout.placeOrder')}
                                </span>
                                <span className="checkout-btn-shine" aria-hidden />
                            </button>
                        </div>
                    </form>

                    <aside className="order-summary-sidebar">
                        <h3 className='order-summary-title'>{t('checkout.orderSummary')}</h3>

                        {/* Cart Items */}
                        <div className='order-items-list'>
                            {cartItems.map((item) => {
                                const sizeLabel = getVariantSizeDisplay(item.selectedVariant?.size, currentLanguage);
                                return (
                                    <div key={item.id || item._id} className='order-item'>
                                        <div className='order-item-info'>
                                    <span className='order-item-name'>
                                        {item.name?.[currentLanguage] || item.name?.en || t('checkout.itemFallback')}
                                        {sizeLabel ? (
                                            <span className='order-item-variant'>({sizeLabel})</span>
                                        ) : null}
                                    </span>
                                            <span className='order-item-qty'>x{item.quantity}</span>
                                        </div>
                                        <span className='order-item-price'>{formatPrice(item.price * item.quantity)}</span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className='order-summary-row'>
                            <span>{t('checkout.subtotal')}</span>
                            <span>{formatPrice(subtotal)}</span>
                        </div>
                        {formData.orderType === 'delivery' && (
                            <div className='order-summary-row'>
                                <span>{t('checkout.deliveryFee')}</span>
                                <span>{formatPrice(deliveryFee)}</span>
                            </div>
                        )}
                        <div className='checkout-coupon-box'>
                            <label htmlFor='couponCode' className='checkout-coupon-label'>{t('checkout.couponCode')}</label>
                            <div className='checkout-coupon-controls'>
                                <input
                                    id='couponCode'
                                    type='text'
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value)}
                                    placeholder={t('checkout.enterCoupon')}
                                    disabled={isApplyingCoupon || isSubmitting}
                                />
                                <button
                                    type='button'
                                    className='checkout-coupon-btn'
                                    onClick={handleApplyCoupon}
                                    disabled={isApplyingCoupon || isSubmitting}
                                >
                                    {isApplyingCoupon ? t('checkout.applying') : t('checkout.apply')}
                                </button>
                            </div>
                            {appliedCoupon && (
                                <button
                                    type='button'
                                    className='checkout-coupon-remove'
                                    onClick={handleRemoveCoupon}
                                >
                                    {t('checkout.removeCoupon')}
                                </button>
                            )}
                            {couponFeedback.message && (
                                <p className={`checkout-coupon-feedback ${couponFeedback.type}`}>
                                    {couponFeedback.message}
                                </p>
                            )}
                        </div>
                        {appliedCoupon && (
                            <div className='order-summary-row order-summary-discount'>
                                <span>{t('checkout.discountWithCode', { code: appliedCoupon.code })}</span>
                                <span>-{formatPrice(pricingDiscountAmount)}</span>
                            </div>
                        )}
                        <div className='order-summary-row'>
                            <span>{taxRowLabel}</span>
                            <span>{formatPrice(tax)}</span>
                        </div>
                        <div className='order-summary-row order-summary-total'>
                            <span>{t('checkout.total')}</span>
                            <span>{formatPrice(totalAmount)}</span>
                        </div>

                        {cartRemainingTime > 0 && (
                            <div className="cart-expiry-notice" role="status">
                                {t('checkout.cartExpiresIn')} {formatTime(cartRemainingTime)}
                            </div>
                        )}
                    </aside>
                </div>
            </section>
        </main>
    );
};

export default Checkout;